import { Lead } from 'models/Lead';
import { User } from 'models/User';
import { DataRoom } from './DataRoom';
import { QAndARoles, QAndASides } from './QAndA';
import { ObjectValues } from 'types';
import { DataRoomDashboardTimeframe } from './Dashboard';
import { AnalyticsTimeframe } from '../subrouters/Reports/pages/Reports/types';

export const DATA_ROOM_MEMBER_ROLES = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  PUBLISHER: 'Publisher',
  VIEWER_DOWNLOADER: 'ViewerDownloader',
  VIEWER: 'Viewer',
} as const;

export type DataRoomMemberRole = ObjectValues<typeof DATA_ROOM_MEMBER_ROLES>;

// Engagement level of a member, derived on the BE from their activity in the
// data room. Rendered as a pill in the members table "Status" column.
export const DATA_ROOM_MEMBER_STATUS = {
  HOT: 'hot',
  COLD: 'cold',
} as const;

export type DataRoomMemberStatus = ObjectValues<typeof DATA_ROOM_MEMBER_STATUS>;

export type DataRoomMembers = {
  sellSideMembers: DataRoomMember[];
  buySideGroups: DataRoomBuySideGroup[];
};

export type RfiActivityAccessDate = {
  dataRoomRfiId: string;
  lastAdditionalFilesViewedAt?: Date;
  lastMessagesViewedAt?: Date;
};

export type DataRoomMember = Pick<User, 'firstName' | 'lastName' | 'email'> & {
  _id: string;

  userId: string;

  canRevoke?: boolean;
  lastAccessNodeAt?: Date; // last time that user access Data room node

  role: DataRoomMemberRole;
  dataRoomId: string;
  leadId: string;
  user: Partial<User> | null;
  dataRoom: Partial<DataRoom> | null;
  lead: Partial<Lead> | null;
  isSetup: boolean;
  isAccepted: boolean;
  uploadedFileIds: string[]; // files that user has uloaded
  accessableDataRoomNodesView: string[];
  accessableDataRoomNodesDownload: string[];
  qaRole: QAndARoles;
  qaSide: QAndASides;
  teams: string[];
  numberOfViewedFiles: number;

  // Engagement stats shown in the members table. All-time totals, optional
  // because they are only present on the members list payload.
  status?: DataRoomMemberStatus;
  viewCount?: number;
  questionCount?: number;
  downloadCount?: number;
  lastSeen?: Date | null;

  isEnterpriseHead?: boolean;
  requestedDataRoomAccess?: boolean;
  requestedDataRoomAccessAt?: Date;

  sessionId?: string;

  isLead?: boolean;
  isLeadWithdrawn?: boolean;

  showConfirmFileInterestBanner?: boolean; // display banner to confirm file access interest

  hasFullAccess?: boolean; // has access to all files in data room
  hasFullDownloadAccess?: boolean; //has download access to all files in data room
  isInReleaseOrder?: boolean; // member tree only; a lead still in a "One at a time" order

  //virtual
  hasAccessToOwnFiles: boolean;
  canExtendAccess?: boolean;

  useMessaging?: boolean; // can use in-platform messaging

  filesAccessExpiredAt?: Date; // date when file access expires

  isPrefferedSummarisedView?: boolean;
  preferredDashboardTimeframe?: DataRoomDashboardTimeframe;

  preferredAnalyticsTimeframe?: AnalyticsTimeframe;

  rfiActivityAccessDates?: RfiActivityAccessDate[];
};

export type DataRoomClosedChatSession = {
  name: string;
  sessionId: string;
};

export type PreviousGroupChats = {
  activeChatSessionId: string;
  closedChatSessions: DataRoomClosedChatSession[];
};

export type DataRoomBuySideGroup = {
  _id: string;

  name: string;

  dataRoomMemberIds: string[];
  dataRoomMembers: DataRoomMember[];
  autoApproveQuestions?: boolean;
  hasQuestionsInReview?: boolean;

  activeChatSessionId: string;
  closedChatSessions?: DataRoomClosedChatSession[];
};
